import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../config/httpConfig";

const getRequestError = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    return err.error || err.message || fallback;
  }
  return fallback;
};

export const fetchManualStatus = createAsyncThunk(
  "bankOperations/fetchManualStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/admin/manual/status");
      return response.response;
    } catch (err) {
      return rejectWithValue(getRequestError(err, "Failed to load manual mode status"));
    }
  }
);

export const fetchRepaymentDeals = createAsyncThunk(
  "bankOperations/fetchRepaymentDeals",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/admin/manual/repayment-deals");
      return response.response;
    } catch (err) {
      return rejectWithValue(getRequestError(err, "Failed to load repayment deals"));
    }
  }
);

export const fetchDepositBusinesses = createAsyncThunk(
  "bankOperations/fetchDepositBusinesses",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/admin/manual/deposit-businesses");
      return response.response;
    } catch (err) {
      return rejectWithValue(getRequestError(err, "Failed to load businesses"));
    }
  }
);

export const fetchRepaymentDealDetails = createAsyncThunk(
  "bankOperations/fetchRepaymentDealDetails",
  async (dealId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/admin/manual/repayment-deal?dealId=${dealId}`);
      return response.response;
    } catch (err) {
      return rejectWithValue(getRequestError(err, "Failed to load repayment deal details"));
    }
  }
);

export const recordManualDeposit = createAsyncThunk(
  "bankOperations/recordManualDeposit",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/admin/manual/deposit", payload);
      return response;
    } catch (err) {
      return rejectWithValue(getRequestError(err, "Failed to record deposit"));
    }
  }
);

export const fetchDepositLookup = createAsyncThunk(
  "bankOperations/fetchDepositLookup",
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/admin/manual/deposit-lookup?orgId=${orgId}`);
      return response.response;
    } catch (err) {
      return rejectWithValue(getRequestError(err, "Failed to load business VA details"));
    }
  }
);

export const fetchDisburseDeals = createAsyncThunk(
  "bankOperations/fetchDisburseDeals",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/admin/manual/disburse-deals");
      return response.response;
    } catch (err) {
      return rejectWithValue(getRequestError(err, "Failed to load deals"));
    }
  }
);

export const fetchDisburseDealDetails = createAsyncThunk(
  "bankOperations/fetchDisburseDealDetails",
  async (dealId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/admin/manual/disburse-deal?dealId=${dealId}`);
      return response.response;
    } catch (err) {
      return rejectWithValue(getRequestError(err, "Failed to load disbursement deal details"));
    }
  }
);

export const confirmManualDisburse = createAsyncThunk(
  "bankOperations/confirmManualDisburse",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/admin/manual/disburse", payload);
      return response;
    } catch (err) {
      return rejectWithValue(getRequestError(err, "Failed to confirm disbursement"));
    }
  }
);

export const fetchPendingWithdrawals = createAsyncThunk(
  "bankOperations/fetchPendingWithdrawals",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/admin/manual/pending-withdrawals");
      return response.response;
    } catch (err) {
      return rejectWithValue(getRequestError(err, "Failed to load withdrawal requests"));
    }
  }
);

export const confirmManualWithdrawal = createAsyncThunk(
  "bankOperations/confirmManualWithdrawal",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/admin/manual/withdrawal", payload);
      return response;
    } catch (err) {
      return rejectWithValue(getRequestError(err, "Failed to confirm withdrawal payout"));
    }
  }
);

export const confirmManualWithdrawalsBulk = createAsyncThunk(
  "bankOperations/confirmManualWithdrawalsBulk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/admin/manual/withdrawals/bulk", payload);
      return response.response ?? response;
    } catch (err) {
      return rejectWithValue(getRequestError(err, "Failed to record withdrawal payouts"));
    }
  }
);

const bankOperationsSlice = createSlice({
  name: "bankOperations",
  initialState: {
    manualStatus: null,
    depositBusinesses: [],
    repaymentDeals: [],
    repaymentDealDetails: null,
    depositLookup: null,
    disburseDeals: [],
    disburseDealDetails: null,
    pendingWithdrawals: [],
    loading: false,
    submitLoading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    clearBankOpsMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    clearRepaymentDealDetails: (state) => {
      state.repaymentDealDetails = null;
    },
    clearDepositLookup: (state) => {
      state.depositLookup = null;
    },
    clearDisburseDealDetails: (state) => {
      state.disburseDealDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchManualStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchManualStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.manualStatus = action.payload;
      })
      .addCase(fetchManualStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchRepaymentDeals.fulfilled, (state, action) => {
        state.repaymentDeals = action.payload || [];
      })
      .addCase(fetchDepositBusinesses.fulfilled, (state, action) => {
        state.depositBusinesses = action.payload || [];
      })
      .addCase(fetchRepaymentDealDetails.fulfilled, (state, action) => {
        state.repaymentDealDetails = action.payload;
      })
      .addCase(fetchDepositLookup.fulfilled, (state, action) => {
        state.depositLookup = action.payload;
      })
      .addCase(fetchDisburseDeals.fulfilled, (state, action) => {
        state.disburseDeals = action.payload || [];
      })
      .addCase(fetchDisburseDealDetails.fulfilled, (state, action) => {
        state.disburseDealDetails = action.payload;
      })
      .addCase(fetchPendingWithdrawals.fulfilled, (state, action) => {
        state.pendingWithdrawals = action.payload || [];
      })
      .addCase(recordManualDeposit.pending, (state) => {
        state.submitLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(recordManualDeposit.fulfilled, (state) => {
        state.submitLoading = false;
        state.successMessage = "Deposit recorded successfully";
      })
      .addCase(recordManualDeposit.rejected, (state, action) => {
        state.submitLoading = false;
        state.error = action.payload;
      })
      .addCase(confirmManualDisburse.pending, (state) => {
        state.submitLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(confirmManualDisburse.fulfilled, (state) => {
        state.submitLoading = false;
        state.successMessage = "Disbursement confirmed successfully";
        state.disburseDealDetails = null;
      })
      .addCase(confirmManualDisburse.rejected, (state, action) => {
        state.submitLoading = false;
        state.error = action.payload;
      })
      .addCase(confirmManualWithdrawal.pending, (state) => {
        state.submitLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(confirmManualWithdrawal.fulfilled, (state) => {
        state.submitLoading = false;
        state.successMessage = "Withdrawal payout recorded successfully";
      })
      .addCase(confirmManualWithdrawal.rejected, (state, action) => {
        state.submitLoading = false;
        state.error = action.payload;
      })
      .addCase(confirmManualWithdrawalsBulk.pending, (state) => {
        state.submitLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(confirmManualWithdrawalsBulk.fulfilled, (state, action) => {
        state.submitLoading = false;
        const successCount = action.payload?.successCount ?? action.payload?.succeeded?.length ?? 0;
        const failedCount = action.payload?.failedCount ?? action.payload?.failed?.length ?? 0;
        if (failedCount === 0) {
          state.successMessage = `${successCount} withdrawal payout(s) recorded successfully`;
        } else if (successCount === 0) {
          state.error = `Failed to record ${failedCount} withdrawal payout(s)`;
        } else {
          state.successMessage = `${successCount} recorded, ${failedCount} failed`;
        }
      })
      .addCase(confirmManualWithdrawalsBulk.rejected, (state, action) => {
        state.submitLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearBankOpsMessages,
  clearRepaymentDealDetails,
  clearDepositLookup,
  clearDisburseDealDetails,
} = bankOperationsSlice.actions;
export default bankOperationsSlice.reducer;
